import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { find } from 'lodash';
import usePageView from '_common/hooks/usePageView';
import {
  CONSUMER_VIEW_FAVORITE_PAGE,
  FAVORITES_SEASON_CAL_CLICKED,
  track
} from 'utils/mixpanel';
import Grid from '_common/pages/search/grid/Grid';
import { PAGE_SIZE } from 'config';
import {
  DeliveryDateContext,
  DELIVERY_DATE_CONTEXT_ISOLATED
} from '_common/context/DeliveryDateContext';
import PageHeader from '@components/page-header/PageHeader';
import ProductCard from '_common/components/product-card/ProductCard';
import { showProductModal } from '_common/reducers/modal';
import { Page } from '@components/page';
import {
  useAppDispatch,
  useAppSelector
} from '../../../_common/hooks/reduxHooks';
import { ContentLoader } from '../../../components/content-loader';
import { CommonNavbar } from '@components/navbar';

const FavoritesPage = () => {
  const { favorites, producers, productsForSale, req } = useAppSelector(
    ({
      auth: { favorites = [] },
      productsForSale: { req, items: productsItems },
      producers: { items: producers }
    }) => {
      const consumersFavorites = productsItems.filter(product => {
        return favorites.some(({ _ref }) => {
          return _ref === product._id;
        });
      });

      return {
        req,
        favorites: consumersFavorites,
        producers,
        productsForSale: productsItems
      };
    }
  );
  const dispatch = useAppDispatch();

  usePageView(CONSUMER_VIEW_FAVORITE_PAGE);

  const [pageCount, setPageCount] = useState(PAGE_SIZE);

  const { t } = useTranslation();
  const context = useMemo(() => {
    return { pageContext: DELIVERY_DATE_CONTEXT_ISOLATED };
  }, []);

  const onProductClick = (productId: string) => {
    track(FAVORITES_SEASON_CAL_CLICKED);
    const producerId = find(productsForSale, { _id: productId })?.producer._id;
    const producerProducts = productsForSale.filter(({ producer: { _id } }) => {
      return _id === producerId;
    });
    const producer = find(producers, { _id: producerId });
    dispatch(
      showProductModal({
        producerProducts,
        producer,
        productId,
        deliveryDateContext: context.pageContext
      }) as any
    );
  };

  return (
    <DeliveryDateContext.Provider value={context}>
      <Page header={<CommonNavbar showLogo />}>
        <PageHeader
          headerText={`${t('consumer:MyFavorites')} (${favorites.length || 0})`}
        />
        <ContentLoader req={req}>
          <Grid
            hasMore={pageCount < favorites.length}
            loadMore={() => {
              return setPageCount(prevCount => {
                return prevCount + PAGE_SIZE;
              });
            }}
          >
            <>
              {favorites.slice(0, pageCount).map(product => {
                return (
                  <ProductCard
                    product={product}
                    key={product._id}
                    onClick={() => {
                      onProductClick(product._id);
                    }}
                  />
                );
              })}
            </>
          </Grid>
        </ContentLoader>
      </Page>
    </DeliveryDateContext.Provider>
  );
};

export default FavoritesPage;
